import React, { useState, useCallback, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Card,
  Upload,
  message,
  Modal,
  Tooltip,
  Checkbox,
  Radio,
  DatePicker,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  CameraOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropImage"; // your helper to get cropped Blob/File
import "../../common.scss";
import UploadCropImage from "../../views/common/commonImage&Crop";
import { publicDonationUploadFile } from "../../views/common/sharedStorageApi";
import { fetchImage } from "../../views/common/downloadUploadImage";
import uploadIcon from "../../assets/icons/file-upload.svg";
import { useTranslation } from "react-i18next";
import UploadImage from "../../views/common/commonImageUpload";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { createApplications } from "../../utility/commonApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

dayjs.extend(utc);
function ApplicationForm({ schema, caseType }) {
  const isSectionedSchema = Array.isArray(schema?.sections);
  const queryClient = useQueryClient();
  let defaultImage = [];
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trustId = localStorage.getItem("trustId");
  const userDetails = useSelector((state) => state.auth);
  const user = userDetails ? userDetails.user : null;
  const [form] = Form.useForm();
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);
  const [commonImageUrl, setCommonImageUrl] = useState([]);
  const fileNames = Array.isArray(uploadedFileUrl)
    ? uploadedFileUrl
        .filter((item) => item && item.fileName)
        .map((item) => item.fileName)
    : [];
  // console.log(fileNames);
  useEffect(() => {
    if (
      Array.isArray(defaultImage) &&
      defaultImage.length > 0 &&
      !refreshFlag
    ) {
      const timer = setTimeout(() => {
        const loadImages = async () => {
          try {
            const urls = await Promise.all(
              defaultImage.map(async (image) => {
                if (!image) return null;
                const url = await fetchImage(image);
                return { url, fileName: image };
              })
            );

            setCommonImageUrl(urls.filter(Boolean));
          } catch (error) {
            console.error("Error loading images:", error);
          }
        };

        loadImages();

        setRefreshFlag(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [defaultImage, refreshFlag]);
  const renderField = (name, config, section = {}) => {
    const excludedFields = ["applicantImage", "aadhar", "maskedAadhar"];

    if (excludedFields.includes(name)) {
      return null; // Skip rendering these fields
    }

    const fieldType = config.fieldType;
    const rules = config.isRequired
      ? fieldType === "checkbox"
        ? [
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error("Required")),
            },
          ]
        : [{ required: true, message: `Required` }]
      : [];

    const placeholder = `Enter ${config.title}`;
    if (section && section.title === "Loan Information") {
      // Make certain fields full width (example: loanDetails)
      if (name === "ifAnyBankLoan") {
        return (
          <Col xs={24} key={name}>
            <Form.Item
              name={name}
              label={
                <span className="commonSmallFont commonFontFamily">
                  {config.title}
                  {config.isRequired && (
                    <span style={{ color: "red" }}> *</span>
                  )}
                </span>
              }
              rules={rules}
            >
              <Input.TextArea rows={4} placeholder={placeholder} />
            </Form.Item>
          </Col>
        );
      }
    }
    if (section && section.title === "Self Declaration") {
      // futurePlans full width
      if (name === "futurePlans") {
        return (
          <Col xs={24} key={name}>
            <Form.Item
              name={name}
              label={
                <span className="commonSmallFont commonFontFamily">
                  {config.title}
                  {config.isRequired && (
                    <span style={{ color: "red" }}> *</span>
                  )}
                </span>
              }
              rules={rules}
            >
              <Input.TextArea rows={4} placeholder={placeholder} />
            </Form.Item>
          </Col>
        );
      }

      // agreement1 and agreement2 full width each
      if (name === "agreement1" || name === "agreement2") {
        return (
          <Col xs={24} key={name}>
            <Form.Item
              name={name}
              valuePropName="checked"
              getValueFromEvent={(event) => event?.target?.checked}
              validateTrigger="onChange"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error("Required")),
                },
              ]}
            >
              <Checkbox
                onChange={(event) => {
                  if (event.target.checked) {
                    form.setFields([{ name, errors: [] }]);
                  }
                }}
              >
                {config.title}
              </Checkbox>
            </Form.Item>
          </Col>
        );
      }

      // date and applicantSignature in one row (2 columns)
      if (name === "date" || name === "applicantSignature") {
        const isDate = name === "date";
        return (
          <Col
            xs={24}
            sm={12}
            key={name}
            style={{ padding: "0 20px", marginBottom: 16 }} // horizontal padding + bottom spacing
          >
            <Form.Item
              name={name}
              rules={rules}
              label={
                <span className="commonSmallFont commonFontFamily">
                  {config.title}
                  {config.isRequired && <span className="text-danger">*</span>}
                </span>
              }
            >
              {isDate ? (
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              ) : (
                <Form.Item
                  name={name}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Required",
                  //   },
                  // ]}
                  getValueFromEvent={(files) => files} // pass uploaded files to form
                >
                  <UploadImage
                    required
                    uploadFileFunction={publicDonationUploadFile}
                    setUploadedFileUrl={(files) => {
                      setUploadedFileUrl(files); // update state
                      form.setFieldsValue({ [name]: files }); // update form value
                    }}
                    name={name}
                    listType="picture"
                    serviceName="application"
                    buttonLabel={t("Upload")}
                    isForApplicationForm
                    initialUploadUrl={commonImageUrl && commonImageUrl}
                    isMultiple={false}
                    maxCount={1}
                    icon={
                      <img
                        src={uploadIcon}
                        alt="Upload Icon"
                        style={{ width: 16, height: 16 }}
                      />
                    }
                    setDeletedImage={setDeletedImages}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        );
      }
      if (name === "applicantSignature") return null;
    }
    if (config.fieldType === "note") {
      return (
        <Col xs={24} key={name}>
          <div className="commonSmallFont commonFontFamily">{config.title}</div>
        </Col>
      );
    }
    if (config.type === "array" && config.items?.type === "object") {
      // Render Form.List for array of objects
      return (
        <Col xs={24} sm={24} md={24} lg={24} xl={24} key={name}>
          <Form.List name={name}>
            {(fields, { add, remove }) => (
              <>
                <label
                  className="commonSmallFont commonFontFamily"
                  style={{ fontWeight: "bold" }}
                >
                  {config.title}{" "}
                  {config.isRequired && (
                    <span style={{ color: "red" }}> *</span>
                  )}
                </label>
                {(fields.length === 0
                  ? [{ key: "default", name: 0, isDefault: true }]
                  : fields
                ).map(({ key, name: fieldName, isDefault, ...restField }) => {
                  const isOnlyOneRow =
                    fields.length === 1 || (fields.length === 0 && isDefault);

                  return (
                    <div
                      key={key}
                      className="array-field-card"
                      style={{ position: "relative" }}
                    >
                      {!isOnlyOneRow && (
                        <DeleteOutlined
                          className="array-field-delete-icon-mobile isMobile"
                          onClick={() => remove(fieldName)}
                        />
                      )}
                      <Row
                        gutter={16}
                        key={key}
                        style={{
                          marginBottom: 8,
                          padding: 10,
                          // border: "1px solid #d9d9d9",
                          borderRadius: 4,
                          alignItems: "center", // vertical center
                        }}
                        className="array-field-row"
                      >
                        {Object.entries(config.items.properties).map(
                          ([subName, subConfig]) => {
                            const subRules = subConfig.isRequired
                              ? [
                                  {
                                    required: true,
                                    message: `${subConfig.title} is required`,
                                  },
                                ]
                              : [];
                            const subPlaceholder = `Enter ${subConfig.title}`;

                            return (
                              <Col
                                xs={24}
                                sm={12}
                                md={12}
                                lg={4}
                                xl={4}
                                key={subName}
                              >
                                <Form.Item
                                  {...restField}
                                  name={[fieldName, subName]}
                                  label={
                                    <span className="commonSmallFont commonFontFamily">
                                      {subConfig.title}
                                      {subConfig.isRequired ? (
                                        <span className="text-danger">*</span>
                                      ) : (
                                        ""
                                      )}
                                    </span>
                                  }
                                  rules={subRules}
                                  style={{ marginBottom: 0 }}
                                >
                                  {subConfig.fieldType === "textarea" ? (
                                    <Input.TextArea
                                      rows={3}
                                      placeholder={subPlaceholder}
                                    />
                                  ) : subConfig.type === "number" ? (
                                    <Input
                                      type="number"
                                      placeholder={subPlaceholder}
                                    />
                                  ) : (
                                    <Input placeholder={subPlaceholder} />
                                  )}
                                </Form.Item>
                              </Col>
                            );
                          }
                        )}

                        <Col
                          xs={24}
                          sm={1}
                          md={1}
                          lg={1}
                          xl={1}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: "25px",
                          }}
                        >
                          {/* Show delete icon only if more than one row */}
                          {!isOnlyOneRow && (
                            <DeleteOutlined
                              style={{
                                color: "red",
                                fontSize: "18px",
                                cursor: "pointer",
                              }}
                              className="isDesktop"
                              onClick={() => remove(fieldName)}
                              title="Delete"
                            />
                          )}
                        </Col>
                      </Row>
                    </div>
                  );
                })}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    <span className="commonSmallFont commonFontFamily">
                      Add
                    </span>
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Col>
      );
    }
    if (
      config.type === "object" &&
      config.items?.type === "object" &&
      config.items.properties
    ) {
      return (
        <Col xs={24} sm={24} md={24} lg={24} xl={24} key={name}>
          <label
            className="commonSmallFont commonFontFamily"
            style={{ fontWeight: "bold" }}
          >
            {config.title}{" "}
            {config.isRequired && <span style={{ color: "red" }}> *</span>}
          </label>

          <div className="array-field-card" style={{ position: "relative" }}>
            <Row
              gutter={16}
              style={{
                marginBottom: 8,
                padding: 10,
                borderRadius: 4,
                alignItems: "center",
              }}
              className="array-field-row"
            >
              {Object.entries(config.items.properties).map(
                ([subName, subConfig]) => {
                  const subRules = subConfig.isRequired
                    ? [
                        {
                          required: true,
                          message: `${subConfig.title} is required`,
                        },
                      ]
                    : [];
                  const subPlaceholder = `Enter ${subConfig.title}`;

                  return (
                    <Col xs={24} sm={12} md={12} lg={4} xl={4} key={subName}>
                      <Form.Item
                        name={[name, subName]}
                        label={
                          <span className="commonSmallFont commonFontFamily">
                            {subConfig.title}
                            {subConfig.isRequired && (
                              <span className="text-danger">*</span>
                            )}
                          </span>
                        }
                        rules={subRules}
                        style={{ marginBottom: 0 }}
                      >
                        {subConfig.fieldType === "textarea" ? (
                          <Input.TextArea
                            rows={3}
                            placeholder={subPlaceholder}
                          />
                        ) : subConfig.type === "number" ? (
                          <Input type="number" placeholder={subPlaceholder} />
                        ) : (
                          <Input placeholder={subPlaceholder} />
                        )}
                      </Form.Item>
                    </Col>
                  );
                }
              )}
            </Row>
          </div>
        </Col>
      );
    }

    let inputComponent;

    // Your existing render logic for other types
    if (config.type === "number") {
      inputComponent = (
        <Input
          type="number"
          style={{ width: "100%" }}
          placeholder={placeholder}
        />
      );
    } else if (config.isUpload === true) {
      inputComponent = (
        <UploadImage
          required
          uploadFileFunction={publicDonationUploadFile}
          setUploadedFileUrl={(files) => {
            setUploadedFileUrl(files); // update state
            form.setFieldsValue({ [name]: files }); // update form value
          }}
          name={name}
          listType="picture"
          serviceName="application"
          buttonLabel={t("Upload")}
          initialUploadUrl={commonImageUrl && commonImageUrl}
          isMultiple={false}
          isForApplicationForm
          maxCount={config.max || 10}
          icon={
            <img
              src={uploadIcon}
              alt="Upload Icon"
              style={{ width: 16, height: 16 }}
            />
          }
          setDeletedImage={setDeletedImages}
        />
      );
    } else if (fieldType === "textarea") {
      inputComponent = (
        <Input.TextArea
          rows={3}
          style={{ width: "100%" }}
          placeholder={placeholder}
        />
      );
    } else if (fieldType === "radio" && Array.isArray(config.enum)) {
      inputComponent = (
        <Radio.Group>
          {config.enum.map((option) => (
            <Radio key={option} value={option}>
              {option}
            </Radio>
          ))}
        </Radio.Group>
      );
    } else if (fieldType === "checkbox") {
      // ✅ Checkbox handling
      inputComponent = (
        <Checkbox
          onChange={(event) => {
            if (event.target.checked) {
              form.setFields([{ name, errors: [] }]);
            }
          }}
        >
          {<span className="commonFontColor">{config.title}</span>}
        </Checkbox>
      );
    } else if (fieldType === "date") {
      inputComponent = (
        <DatePicker
          style={{ width: "100%" }}
          format="DD MMM YYYY"
          placeholder={placeholder}
        />
      );
    } else {
      inputComponent = <Input placeholder={placeholder} />;
    }
    const label = name !== "agreement1" && name !== "agreement2" && (
      <>
        <span className="commonSmallFont commonFontFamily">{config.title}</span>
        {config.isRequired && <span style={{ color: "red" }}> *</span>}
      </>
    );

    return (
      <Col xs={24} sm={12} md={12} lg={8} xl={8} key={name}>
        <Form.Item
          name={name}
          label={label}
          rules={rules}
          valuePropName={fieldType === "checkbox" ? "checked" : undefined}
          getValueFromEvent={
            fieldType === "checkbox"
              ? (event) => event?.target?.checked
              : undefined
          }
          validateTrigger={fieldType === "checkbox" ? "onChange" : undefined}
        >
          {inputComponent}
        </Form.Item>
      </Col>
    );
  };

  const [applicantFileUrl, setApplicantFileUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState([]);
  const fileName =
    Array.isArray(applicantFileUrl) && applicantFileUrl.length > 0
      ? applicantFileUrl.find((item) => item && item.fileName)?.fileName || null
      : null;
  //   useEffect(() => {
  //     const loadImage = async () => {
  //       try {
  //         if (initialValues?.image) {
  //           const imageName = initialValues.image;
  //           const url = await fetchImage(imageName);

  //           if (url) {
  //             const fileData = { url, fileName: imageName };
  //             setImageUrl([fileData]);
  //             setUploadFileUrl([fileData]);
  //           }
  //         } else {
  //           setImageUrl([]);
  //           setUploadFileUrl(null);
  //         }
  //       } catch (error) {
  //         console.error("Error loading image:", error);
  //       }
  //     };

  //     loadImage();
  //   }, [initialValues?.image]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    // console.log(values)
    // return
    setIsSubmitting(true); // start loading

    const { amountRequired, ...restValues } = values;
    const uploadedDocuments = [];
    const cleanedValues = { ...restValues };
    const timeline = dayjs().utc().startOf("day").toISOString();
    // Remove aadharCardImage and panCardImage from the top-level values
    delete cleanedValues.aadharCardImage;
    delete cleanedValues.panCardImage;
    delete cleanedValues.aadhar;
    delete cleanedValues.maskedAadhar;

    // Safely transform applicantFileUrl
    if (Array.isArray(applicantFileUrl) && applicantFileUrl.length > 0) {
      applicantFileUrl.forEach((file) => {
        if (file?.fileName) {
          uploadedDocuments.push({
            fieldName: file?.name || "applicantImage", // fallback
            path: file.fileName,
          });
        }
      });
    }

    // Safely transform uploadedFileUrl
    if (Array.isArray(uploadedFileUrl) && uploadedFileUrl.length > 0) {
      uploadedFileUrl.forEach((file) => {
        if (file?.fileName && file?.name) {
          uploadedDocuments.push({
            fieldName: file.name,
            path: file.fileName,
          });
        }
      });
    }

    // Final payload
    const payload = {
      createdBy: user ? user._id : "",
      trustId,
      timeline,
      caseType,
      amountRequired,
      dynamicData: { ...cleanedValues },
      uploadedDocuments,
    };
    // return
    try {
      const res = await createApplications(payload);
      queryClient.invalidateQueries("MyApplications");
      if (res?.error) {
        toast.error("Failed to Create application.");
      } else {
        toast.success("Application Create successfully.");
        setApplicantFileUrl([]);
        navigate("/home?page=myApplications");
        form.resetFields(); // <-- reset form here after success
      }
    } catch (err) {
      toast.error("An error occurred while creating.");
      console.error("Update error:", err);
    } finally {
      setIsSubmitting(false);
    }
    console.log("Final Payload:", payload);
  };

  const regularFieldsOld = schema?.properties
    ? Object.entries(schema.properties).filter(
        ([_, config]) =>
          config.isUpload !== true && config.title !== "Applicant Image"
      )
    : [];

  // For new schema (inside sections)
  const regularFieldsNew = schema?.sections
    ? schema.sections.flatMap((section) =>
        section.properties
          ? Object.entries(section.properties).filter(
              ([_, config]) =>
                config.isUpload !== true && config.title !== "Applicant Image"
            )
          : []
      )
    : [];

  // Merge both (whichever exists will be used)
  const regularFields = [...regularFieldsOld, ...regularFieldsNew];

  // For old schema
  const uploadFieldsOld = schema?.properties
    ? Object.entries(schema.properties).filter(
        ([_, config]) => config.isUpload === true
      )
    : [];

  // For new schema
  const uploadFieldsNew = schema?.sections
    ? schema.sections.flatMap((section) =>
        section.properties
          ? Object.entries(section.properties).filter(
              ([_, config]) => config.isUpload === true
            )
          : []
      )
    : [];

  // Merge both
  const uploadFields = [...uploadFieldsOld, ...uploadFieldsNew];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        initialValues={{ amountRequired: "" }} // optional default
      >
        {isSectionedSchema ? (
          schema.sections.map((section, sectionIdx) => {
            const hasApplicantImage = Object.keys(section.properties).includes(
              "applicantImage"
            );
            const sectionLetter = String.fromCharCode(65 + sectionIdx);

            return (
              <Card
                key={sectionIdx}
                title={
                  <span className="commonFontColor commonFont">
                    {sectionLetter}. {section.title.toUpperCase()}
                  </span>
                }
                style={{ marginBottom: 24 }}
                className="form-section-card"
              >
                <Row gutter={[24, 16]}>
                  {/* Left Side Fields */}
                  <Col xs={24} md={hasApplicantImage ? 18 : 24}>
                    <Row gutter={[24, 16]}>
                      {Object.entries(section.properties).map(
                        ([name, config]) => {
                          if (name === "applicantImage") return null; // skip image here
                          return renderField(name, config, section);
                        }
                      )}
                    </Row>
                  </Col>

                  {/* Right Side Applicant Image */}
                  {hasApplicantImage && (
                    <Col xs={24} md={6} className="applicant-upload-col">
                      <UploadCropImage
                        required
                        uploadFileFunction={publicDonationUploadFile}
                        setUploadedFileUrl={setApplicantFileUrl}
                        name="applicantImage"
                        listType="picture"
                        serviceName="application"
                        buttonLabel={t("Upload")}
                        initialUploadUrl={imageUrl.length > 0 ? imageUrl : []}
                        isMultiple={false}
                        trustId={trustId}
                        maxCount={1}
                        isCropping={true}
                        cropAspect={7 / 9}
                        icon={
                          <img
                            src={uploadIcon}
                            alt="Upload Icon"
                            style={{ width: 16, height: 16 }}
                          />
                        }
                      />
                    </Col>
                  )}
                </Row>
              </Card>
            );
          })
        ) : (
          <Row gutter={[24, 16]} className="application-form-row">
            {/* Left Fields */}

            <Col xs={24} md={18} className="form-fields-col">
              <Row gutter={[24, 16]}>
                {regularFields.map(([name, config]) =>
                  renderField(name, config)
                )}
                {/* <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="amountRequired"
                  label={
                    <>
                      <span className="commonSmallFont commonFontFamily">
                        Amount Required
                      </span>
                      <span style={{ color: "red" }}> *</span>
                    </>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Amount Required is required",
                    },
                  ]}
                >
                  <Input
                    type="number"
                    placeholder="Enter Amount Required"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col> */}
              </Row>

              <Row gutter={[24, 16]} className="mt-2">
                {uploadFields.map(([name, config]) =>
                  renderField(name, config)
                )}
              </Row>
            </Col>

            {/* Right Upload */}
            <Col xs={24} md={6} className="applicant-upload-col">
              <UploadCropImage
                required
                uploadFileFunction={publicDonationUploadFile}
                setUploadedFileUrl={setApplicantFileUrl}
                name="applicantImage"
                listType="picture"
                serviceName="application"
                buttonLabel={t("Upload")}
                initialUploadUrl={imageUrl.length > 0 ? imageUrl : []}
                isMultiple={false}
                trustId={trustId}
                maxCount={1}
                isCropping={true}
                cropAspect={7 / 9}
                icon={
                  <img
                    src={uploadIcon}
                    alt="Upload Icon"
                    style={{ width: 16, height: 16 }}
                  />
                }
              />
            </Col>
          </Row>
        )}
        <div className="submit-btn-col d-flex justify-content-center w-100">
          <Form.Item className="mt-3">
            <Button
              type="primary"
              htmlType="submit"
              className="addAction-btn2"
              style={{ height: "35px" }}
              loading={isSubmitting}
            >
              Submit Application
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}

export default ApplicationForm;
